import MuiBox from '@mui/material/Box';
import MuiListItemButton from '@mui/material/ListItemButton';
import { alpha, type Theme } from '@mui/material/styles';
import type { ReactNode } from 'react';

export interface ResultListItemProps {
  icon?: ReactNode;
  // The row's accessible name, and its only line. A result row that needed a second line would
  // be ActionListItem, not this.
  label: string;
  onClick: () => void;
  trailing?: ReactNode;
}

// A one-line, keyboard-operable result row. It exists because neither half can be composed from
// the rest of the façade: ActionListItem requires a description and a 40px icon chip, and Box
// exposes no onClick — so there was no way to build an accessibly-named, activatable row at all.
// The glyph sits INLINE with the text rather than in ActionListItem's 40px rounded chip: these
// rows are a scannable list of addresses, and a chip per row would turn a list into a stack of
// cards. The hover tint is the brand at 8%, resolved from the theme — never a hex.
const rowSx = (theme: Theme) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  // The address is what the agent scans for, so it carries the weight and the glyphs on either
  // side of it recede — a column of rows is only readable at a glance if one thing in each row is
  // the thing to read. Both resolved from the theme, never a hex or a literal weight: the system
  // reads `'fontWeightBold'` as a key into `theme.typography`, which is also the one spelling that
  // type-checks — the property itself is optional there, so reaching for it directly widens to
  // `| undefined` and sx's index signature refuses it.
  fontWeight: 'fontWeightBold',
  '& svg': { color: theme.palette.text.secondary },
  '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
});

// The label takes every pixel the glyphs leave, which is what lands the trailing slot hard against
// the row's right edge — and what makes the LABEL the element that gives way when the address is
// longer than the row. Deliberately not an auto margin on the row's last child: with no trailing
// slot that selector lands on the label and pushes the address right instead.
//
// `minWidth: 0` is the load-bearing one. A flex item refuses to shrink below its content by
// default, so without it a long address either pushes the chevron out of the column or wraps to a
// second line — and either way one row stops matching the height of every other, which is the
// rhythm the list is read by. The other three only take effect once it can shrink.
const labelSx = {
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export function ResultListItem({ icon, label, onClick, trailing }: ResultListItemProps) {
  return (
    <MuiListItemButton onClick={onClick} sx={rowSx}>
      {icon}
      {/* `title`: CSS truncation hides the tail from the eye but not from the accessible name, and
          two portals of one building can differ only in that tail — so a hover recovers it. */}
      <MuiBox component="span" sx={labelSx} title={label}>
        {label}
      </MuiBox>
      {trailing}
    </MuiListItemButton>
  );
}
